/**
 * Aspects between the ANGLES — Ascendant and Midheaven — and the planets.
 *
 * <h2>A CROSSING, not an orb</h2>
 *
 * The Vedic lines are house-to-house: a planet either aspects a house or it does not, and there is
 * no "nearly". These are degree-to-degree, and the exact aspect is an instant — so the question is
 * not "how close is it now" but "did the exact angle happen between the last frame and this one".
 *
 * <p>That makes an orb the wrong tool and a crossing the right one. An orb has to be chosen, and
 * whatever it is chosen to be it is wrong twice: too wide and a line appears for a degree either
 * side of an aspect that has not happened, too narrow and an aspect that DID happen between two
 * frames leaves no line at all, because neither frame was sampled close enough to exact. A crossing
 * has no such number in it. If the animation steps four minutes and the exact aspect falls anywhere
 * inside those four minutes, the line is drawn on that step — once, at the step it occurred in.
 *
 * <p>The consequence worth knowing: with no previous frame there is no interval, so nothing is
 * drawn until the chart has advanced once. A still chart shows no angular lines, and that is
 * correct rather than a failure to load — an instant has no duration for an aspect to fall inside.
 *
 * <h2>Longitudes decide, houses only draw</h2>
 *
 * The aspect is found in degrees and then rendered as a line between two CELLS, which is the same
 * compromise the Vedic lines already make on this chart: it is a North-Indian square, twelve house
 * cells and no degree axis to put an endpoint on.
 *
 * <p>That compromise has one visible consequence and it is worth stating rather than discovering: a
 * planet conjunct the Ascendant sits in house 1, which is the Ascendant's own cell, so the line
 * would have zero length. Those are dropped. Nothing is lost — a conjunction with the Ascendant is
 * exactly the case the chart already shows plainly, with the planet and the "Asc" label in the same
 * box — but a reader counting lines should know why there is no line for it.
 *
 * <h2>Hard and soft are separated on purpose</h2>
 *
 * Conjunction, square and opposition carry one pen; the trine carries another, thicker one. Two
 * channels rather than one: the width survives a monochrome screenshot and a reader who cannot
 * separate blue from amber, and on a chart where several lines converge on house 1 the weight is
 * what tells them apart at a glance.
 */

/** Conjunction, square, opposition — the hard angles. */
export const HARD_ANGLES = [0, 90, 180];

/** The soft one. */
export const TRINE_ANGLE = 120;

export const HARD_ASPECT_COLOR = '#2563eb';
export const HARD_ASPECT_WIDTH = 1.5;
export const TRINE_ASPECT_COLOR = '#f59e0b';
export const TRINE_ASPECT_WIDTH = 3;

/** The two angles, in the order they are drawn. */
export const ANGLE_KEYS = ['Asc', 'MC'];

/** Shortest separation between two longitudes, 0..180. */
export function separation(a, b) {
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  const d = (((a - b) % 360) + 360) % 360;
  return d > 180 ? 360 - d : d;
}

/**
 * Did the short arc from `prev` to `curr` pass over `target`?
 *
 * <p>The same rule the 24-hour scan uses, lifted here so both halves of this dialog agree about
 * what a crossing is. The 180-degree test is what decides which way round the arc went: an angle
 * moving 350 -> 010 has gone forward over zero, not backwards through 180.
 */
export function crossedLongitude(prev, curr, target) {
  if (!Number.isFinite(prev) || !Number.isFinite(curr) || !Number.isFinite(target)) return false;
  const p = (((prev % 360) + 360) % 360);
  const c = (((curr % 360) + 360) % 360);
  const t = (((target % 360) + 360) % 360);
  if (p === c) return false;
  const lo = Math.min(p, c);
  const hi = Math.max(p, c);
  if (hi - lo <= 180) return t > lo && t < hi;
  return t > hi || t < lo;      // the short arc crosses 0
}

/** The pen for an aspect angle. */
export function penFor(angle) {
  const trine = angle === TRINE_ANGLE;
  return {
    kind: trine ? 'trine' : 'hard',
    color: trine ? TRINE_ASPECT_COLOR : HARD_ASPECT_COLOR,
    width: trine ? TRINE_ASPECT_WIDTH : HARD_ASPECT_WIDTH,
  };
}

/**
 * Which house the Midheaven falls in, on a whole-sign chart.
 *
 * <p>Exported so the renderer and the aspect finder cannot disagree about it. The Ascendant is
 * house 1 by definition; the Midheaven is wherever its longitude lands, and a line drawn from the
 * wrong cell is a line pointing at the wrong planet.
 */
export function mcHouseFor(chartData) {
  const houses = Array.isArray(chartData?.houses) ? chartData.houses : [];
  const lon = chartData?.mcLon;
  if (!Number.isFinite(lon) || houses.length === 0) return null;
  const signIdx = Math.floor(lon / 30) + 1;
  const match = houses.find(h => h.signIndex === signIdx);
  return match ? match.house : null;
}

/**
 * Every angle-to-planet aspect whose exact moment fell between two frames.
 *
 * <p>No orb: each aspect angle becomes a target longitude on either side of the planet, and the
 * line is drawn when the Ascendant or Midheaven CROSSED that target during the step. At four
 * minutes a step the Ascendant moves about a degree, so an aspect that happened anywhere inside it
 * is caught — and one that did not happen produces nothing, however near the chart currently is.
 *
 * <p>The planet is treated as fixed across the step, which is the same approximation the 24-hour
 * scan makes and is sound for the same reason: the Ascendant moves roughly a degree in four minutes
 * and the slowest-crossing body moves a small fraction of that. Using the planet's CURRENT
 * longitude as the target rather than its previous one keeps the target on the side the aspect
 * actually became exact.
 *
 * <p>Both sides of every angle are targeted — 270 as well as 90, 240 as well as 120 — because the
 * separation reaches a given value from either direction and scanning one side would silently miss
 * half of every square and half of every trine. They fold back for the label.
 *
 * @param {object} prev the previous frame's chart, or null on the first frame
 * @param {object} curr this frame's chart
 * @param {number[]} offsets which target offsets to look for
 * @returns {Array<{from:number,to:number,color:string,width:number,angle:number,label:string}>}
 */
export function angularAspectLines(prev, curr, offsets = SCAN_MODES.ascmc.offsets) {
  const planets = Array.isArray(curr?.planets) ? curr.planets : [];
  if (!prev || planets.length === 0) return [];
  const mcHouse = mcHouseFor(curr);
  const anchors = [
    { key: 'Asc', prevLon: prev?.ascendantLon, lon: curr?.ascendantLon, house: 1 },
    { key: 'MC', prevLon: prev?.mcLon, lon: curr?.mcLon, house: mcHouse },
  ];

  const byKey = new Map();
  anchors.forEach((anchor) => {
    if (!Number.isFinite(anchor.prevLon) || !Number.isFinite(anchor.lon)) return;
    if (!Number.isFinite(anchor.house)) return;
    planets.forEach((p) => {
      const lon = p?.siderealLon;
      if (!Number.isFinite(lon) || !Number.isFinite(p?.house)) return;
      // A line inside one cell has nowhere to go. See the note at the top of this file.
      if (p.house === anchor.house) return;

      offsets.forEach((off) => {
        if (!crossedLongitude(anchor.prevLon, anchor.lon, lon + off)) return;
        const angle = foldOffset(off);
        const pen = penFor(angle);
        const key = `${anchor.house}|${p.house}|${angle}`;
        const existing = byKey.get(key);
        if (existing) {
          if (!existing.bodies.includes(`${anchor.key}-${p.abbr}`)) {
            existing.bodies.push(`${anchor.key}-${p.abbr}`);
            existing.label = existing.bodies.join(', ');
          }
          return;
        }
        byKey.set(key, {
          from: anchor.house,
          to: p.house,
          color: pen.color,
          width: pen.width,
          dash: 'none',
          angle,
          kind: pen.kind,
          bodies: [`${anchor.key}-${p.abbr}`],
          label: `${anchor.key}-${p.abbr} ${angle}°`,
          abbr: anchor.key,
          offset: angle,
        });
      });
    });
  });
  return [...byKey.values()];
}

/* ───────────────────────────── the Compute Aspects scan's angle set ──────────────────────────── */

/**
 * Which offsets the 24-hour scan looks for, by mode.
 *
 * <p><b>Kendra IS the hard set.</b> The kendras are the angular houses — 1, 4, 7 and 10 — which are
 * the 0, 90, 180 relationships and nothing else. So the existing scan was already "every exact time
 * the Ascendant or Midheaven makes a hard aspect to a planet", and the new mode differs from it by
 * one angle: the trine. That is why this is an offset list rather than a second scanner. Writing a
 * parallel engine for it would have been two implementations of one crossing search, free to
 * disagree about a minute.
 *
 * <p>270 is present alongside 90 because the scan detects a CROSSING of a target longitude, and an
 * angle can reach 90 degrees of separation from either side. Same for 240 and the trine. They fold
 * back to 90 and 120 when the row is labelled.
 */
export const SCAN_MODES = {
  kendra: { key: 'kendra', label: 'Kendra', offsets: [0, 90, 180, 270] },
  ascmc: { key: 'ascmc', label: 'Asc and MC', offsets: [0, 90, 120, 180, 240, 270] },
};

/** The separation an offset represents once folded to 0..180. */
export function foldOffset(offset) {
  return offset > 180 ? 360 - offset : offset;
}

/** Column headings for the two tables, which say which question was asked. */
export function scanTableHeadings(modeKey) {
  return modeKey === SCAN_MODES.ascmc.key
    ? { asc: 'Asc aspects', mc: 'MC aspects' }
    : { asc: 'Kendra w/ Asc', mc: 'Kendra w/ MC' };
}
